"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DataFilter, type FilterValues } from "@/components/data-filter";
import { DataTable, type ColumnDef } from "@/components/data-table";
import { DataSummary } from "@/components/data-summary";
import { fetchCrmContacts, exportCrmContacts } from "./actions";
import { CRM_COLUMNS } from "./columns";
import type { CrmContactRow } from "@/lib/query";
import { Download } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "审核完成", label: "审核完成" },
  { value: "审核中", label: "审核中" },
  { value: "未发起", label: "未发起" },
  { value: "拒绝", label: "拒绝" },
  { value: "撤回申请", label: "撤回申请" },
];

const TABLE_COLUMNS: ColumnDef<CrmContactRow>[] = CRM_COLUMNS.map((col) => ({
  key: col.key,
  header: col.label,
  className: col.key === "vendor_name" || col.key === "description"
    ? "min-w-[150px]"
    : col.key === "amount" || col.key === "total_amount"
      ? "text-right min-w-[100px]"
      : undefined,
  render: col.key === "amount" || col.key === "total_amount"
    ? (row: CrmContactRow) => {
        const val = row[col.key as keyof CrmContactRow];
        return val !== null ? `¥${Number(val).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}` : "";
      }
    : undefined,
}));

export default function CrmContactsPage() {
  const [filters, setFilters] = useState<FilterValues>({
    documentNumber: "",
    vendorName: "",
    startDate: "",
    endDate: "",
    status: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<CrmContactRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ count: 0, totalAmount: 0, taxAmount: 0, grandTotal: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCrmContacts(filters, page, pageSize);
      setData(result.rows);
      setTotal(result.total);
      setSummary(result.summary);
    } catch (err) {
      console.error("查询失败:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function handleSearch() {
    setPage(1);
    loadData();
  }

  function handlePageSizeChange(size: number) {
    setPageSize(size);
    setPage(1);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const result = await exportCrmContacts(filters);
      const blob = new Blob([result.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("导出失败:", err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex-1 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">CRM 客户联系人变更</h1>
        <Button onClick={handleExport} disabled={exporting} size="sm">
          <Download className="h-4 w-4 mr-1" />
          {exporting ? "导出中..." : "导出 CSV"}
        </Button>
      </div>

      <DataFilter
        filters={filters}
        onFiltersChange={setFilters}
        statusOptions={STATUS_OPTIONS}
      />

      <Button onClick={handleSearch} disabled={loading} size="sm" className="w-full md:w-auto">
        {loading ? "查询中..." : "查询"}
      </Button>

      <DataSummary summary={summary} />

      <DataTable
        columns={TABLE_COLUMNS}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}